import type { MutableRefObject } from "react";
import type { VoiceRuntimeState } from "../types/voice";

interface VoiceTranscribeClient {
  voice: {
    transcribe(data: {
      audioBase64: string;
      mimeType: "audio/webm";
    }): Promise<{ text?: string } | null>;
  };
}

interface LocalVoiceSttClient {
  transcribe: (
    audioBase64: string,
    mimeType: string,
  ) => Promise<{ ok: boolean; text?: string; error?: string }>;
}

export interface ProcessVoiceRecordingInput {
  chunks: Blob[];
  backend: VoiceTranscribeClient;
  enabledRef: MutableRefObject<boolean>;
  busyRef: MutableRefObject<boolean>;
  setRuntimeState: (state: VoiceRuntimeState) => void;
  setLastUserText: (text: string) => void;
  setErrorState: () => void;
  startListening: () => void;
  onTranscript?: (text: string) => void;
  sendText: (text: string, mode: "chat") => void;
  localStt?: LocalVoiceSttClient;
}

function blobToBase64(blob: Blob): Promise<string> {
  return blob
    .arrayBuffer()
    .then((arrayBuffer) =>
      btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      ),
    );
}

async function blobToPcmWavBase64(blob: Blob): Promise<string> {
  const AudioContextConstructor =
    window.AudioContext ||
    (
      window as Window & {
        webkitAudioContext?: typeof AudioContext;
      }
    ).webkitAudioContext;
  if (!AudioContextConstructor) {
    throw new Error("This desktop runtime cannot normalize microphone audio");
  }

  const audioContext = new AudioContextConstructor();
  try {
    const decoded = await audioContext.decodeAudioData(
      await blob.arrayBuffer(),
    );
    return blobToBase64(
      new Blob([encodePcmWav(decoded, 16_000)], { type: "audio/wav" }),
    );
  } finally {
    await audioContext.close();
  }
}

function encodePcmWav(
  audio: AudioBuffer,
  targetSampleRate: number,
): ArrayBuffer {
  const frameCount = Math.max(
    1,
    Math.round((audio.length / audio.sampleRate) * targetSampleRate),
  );
  const buffer = new ArrayBuffer(44 + frameCount * 2);
  const view = new DataView(buffer);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + frameCount * 2, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, targetSampleRate, true);
  view.setUint32(28, targetSampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, frameCount * 2, true);

  for (let frame = 0; frame < frameCount; frame += 1) {
    const position = (frame * audio.sampleRate) / targetSampleRate;
    const start = Math.min(Math.floor(position), audio.length - 1);
    const end = Math.min(start + 1, audio.length - 1);
    const ratio = position - start;
    let mixed = 0;
    for (let channel = 0; channel < audio.numberOfChannels; channel += 1) {
      const samples = audio.getChannelData(channel);
      mixed += samples[start] + (samples[end] - samples[start]) * ratio;
    }
    const clamped = Math.max(-1, Math.min(1, mixed / audio.numberOfChannels));
    view.setInt16(44 + frame * 2, Math.round(clamped * 32_767), true);
  }
  return buffer;
}

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

export async function processVoiceRecordingFallback({
  chunks,
  backend,
  enabledRef,
  busyRef,
  setRuntimeState,
  setLastUserText,
  setErrorState,
  startListening,
  onTranscript,
  sendText,
  localStt,
}: ProcessVoiceRecordingInput): Promise<void> {
  if (chunks.length === 0 || !enabledRef.current) return;

  const blob = new Blob(chunks, { type: "audio/webm;codecs=opus" });
  if (blob.size < 8000) {
    console.debug("[VoiceAssistant] Recording too short, skipping");
    if (enabledRef.current && !busyRef.current) startListening();
    return;
  }

  try {
    setRuntimeState("thinking");
    busyRef.current = true;
    const audioBase64 = localStt
      ? await blobToPcmWavBase64(blob)
      : await blobToBase64(blob);
    let result: { ok?: boolean; text?: string; error?: string } | null;
    if (localStt) {
      result = await localStt.transcribe(audioBase64, "audio/wav");
      if (!result.ok) {
        throw new Error(result.error || "Local voice input unavailable");
      }
    } else {
      result = await backend.voice.transcribe({
        audioBase64,
        mimeType: "audio/webm",
      });
    }

    const transcript = result?.text?.trim();
    if (!transcript) {
      console.debug("[VoiceAssistant] Transcription returned empty text");
      busyRef.current = false;
      if (enabledRef.current) {
        setRuntimeState("listening");
        startListening();
      }
      return;
    }

    busyRef.current = false;
    onTranscript?.(transcript);
    setLastUserText(transcript);
    sendText(transcript, "chat");
  } catch (err) {
    console.error("[VoiceAssistant] Transcription failed:", err);
    busyRef.current = false;
    setErrorState();
  }
}
